import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskReveal, Reveal, MaskRevealInView } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { IMAGES, SERVICES, PROCESS, CREDENTIALS, HERO, BRAND } from "../data";

const ease = [0.19, 1, 0.22, 1];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section ref={ref} data-testid="hero" className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="font-sans text-[11px] md:text-xs tracking-[0.4em] uppercase text-rosedeep mb-8 md:mb-12"
        >
          {BRAND.tagline}
        </motion.p>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5rem] leading-[0.98] text-ink tracking-tight">
              <MaskReveal lines={["Helping brands grow"]} delay={0.2} />
              <MaskReveal lines={["through strategy,"]} delay={0.32} />
              <span className="block">
                <span className="mask-line inline-block">
                  <motion.span
                    className="block italic font-display text-rosedeep"
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, ease, delay: 0.44 }}
                  >
                    content &amp; community.
                  </motion.span>
                </span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85 }}
              className="mt-8 font-sans text-lg leading-relaxed text-mutedfg max-w-xl"
            >
              {HERO.subheading}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center"
            >
              <Link
                to="/work-with-me"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-rosedeep text-white px-8 py-4 font-sans text-[12px] tracking-[0.16em] uppercase transition-colors duration-300 hover:bg-ink"
              >
                Work With Me
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 px-8 py-4 font-sans text-[12px] tracking-[0.16em] uppercase text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
              >
                View Services
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease }}
              className="relative"
            >
              <div className="relative overflow-hidden bg-blush aspect-[4/5]">
                <motion.img
                  src={IMAGES.hero}
                  alt="Roisin Doyle"
                  style={{ y, scale }}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutTeaser = () => (
  <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-32">
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-3">
        <Reveal>
          <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-mutedfg sticky top-28">(About)</p>
        </Reveal>
      </div>
      <div className="lg:col-span-9">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-[3.2rem] leading-[1.14] text-ink text-balance">
          <MaskRevealInView
            lines={[
              "A boutique consultancy helping",
              "purpose-driven brands communicate",
            ]}
          />
          <span className="mask-line inline-block">
            <motion.span
              className="block italic font-display text-rosedeep"
              initial={{ y: "110%" }}
              whileInView={{ y: "0%" }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease, delay: 0.3 }}
            >
              clearly and grow with intention.
            </motion.span>
          </span>
        </h2>
        <Reveal delay={0.2}>
          <Link
            to="/about"
            data-testid="home-about-link"
            className="group inline-flex items-center gap-2 mt-10 font-sans text-[12px] tracking-[0.18em] uppercase text-ink hover-underline"
          >
            More about Roisin <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesSnapshot = () => (
  <section className="bg-mutewarm py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
        <Reveal>
          <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-rosedeep mb-6">Services</p>
          <h2 className="font-serif text-4xl md:text-6xl text-ink leading-none">How I can help</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/services"
            data-testid="home-services-link"
            className="group inline-flex items-center gap-2 font-sans text-[12px] tracking-[0.18em] uppercase text-ink hover-underline"
          >
            All services <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-borderwarm border border-borderwarm">
        {SERVICES.slice(0, 6).map((s, i) => (
          <Reveal key={s.n} delay={(i % 3) * 0.06} className="bg-white">
            <div className="group h-full p-8 lg:p-9 transition-colors duration-500 hover:bg-blush">
              <span className="font-sans text-[11px] tracking-[0.2em] text-rosedeep">{s.n}</span>
              <h3 className="mt-6 font-serif text-2xl text-ink leading-tight">{s.title}</h3>
              <p className="mt-4 font-sans text-sm leading-relaxed text-mutedfg">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyMe = () => (
  <section className="bg-wine text-blush py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
      <Reveal>
        <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-rose mb-6">Why Work With Me</p>
        <h2 className="font-serif text-4xl md:text-6xl leading-none text-white mb-14 md:mb-20 max-w-2xl">
          Strategy, credibility and a genuine care for your brand.
        </h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8">
        {CREDENTIALS.map((c, i) => (
          <Reveal key={c} delay={(i % 3) * 0.06}>
            <div className="flex items-start gap-4 border-t border-white/15 pt-5">
              <span className="font-sans text-[11px] text-rose mt-1">0{i + 1}</span>
              <p className="font-serif text-xl md:text-2xl leading-snug text-white">{c}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProcessHome = () => (
  <section className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-36">
    <Reveal>
      <p className="font-sans text-[11px] tracking-[0.35em] uppercase text-rosedeep mb-6">The Process</p>
      <h2 className="font-serif text-4xl md:text-6xl text-ink leading-none mb-14 md:mb-20">How we'll work together</h2>
    </Reveal>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-borderwarm border border-borderwarm">
      {PROCESS.map((p, i) => (
        <Reveal key={p.n} delay={(i % 4) * 0.08} className="bg-white">
          <div className="h-full p-8 lg:p-10">
            <span className="font-serif text-6xl md:text-7xl text-rose">{p.n}</span>
            <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ink">{p.title}</h3>
            <p className="mt-4 font-sans text-sm leading-relaxed text-mutedfg">{p.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <div className="border-y border-borderwarm text-ink">
        <Marquee items={["Strategy", "Content", "Community", "Brand", "Partnerships"]} />
      </div>
      <AboutTeaser />
      <ServicesSnapshot />
      <WhyMe />
      <ProcessHome />
    </>
  );
}
